/**
 * The two-domain game clock (architecture §2.3, §5.3; Acceptance test 4).
 *
 * - **Logic** advances in whole ticks: `simMs` is always an exact multiple of `SIM_DT` (stored as a tick count, so
 *   6 ticks are exactly 100 ms, not 100.00000000000001) and only `commitTick()` moves it. Ticks make every decision.
 * - **Presentation** is a continuous instant `presentMs`. After the frame's tick loop
 *   `presentMs ≤ simMs < presentMs + SIM_DT`: each rendered frame evaluates animation, camera, VFX and HTML at exactly
 *   that instant. Nothing is quantized to 16.7 ms.
 *
 * `step(ms)` / `seek(ms)` move `presentMs` by EXACTLY ms (any ms ≥ 0, e.g. 1000/144) and make the next frame run every
 * tick it needs, uncapped. Wall-clock systems read `wallNow()` at the app level and pass the value into pure code.
 *
 * Pure: the wall source is injected (the app passes `() => Date.now()`); this file never touches `Date`.
 */

/** Logic tick, ms (60 Hz). */
export const SIM_DT = 1000 / 60;
/** Spiral-of-death guard: at most this many logic ticks per real-time frame (stepped frames are never capped). */
export const MAX_TICKS_PER_FRAME = 5;
/** Tab switch / breakpoint clamp of a real frame's dt, ms. */
export const MAX_FRAME_DT = 250;
/** Allowed `timeScale` range (debug `__marg.setTimeScale`, `?timeScale=`). */
export const TIME_SCALE_MIN = 0.05;
export const TIME_SCALE_MAX = 8;
/** Tolerance of the "logic frontier covers the display instant" test, ms. */
export const FRONTIER_EPS = 1e-6;

export interface ClockOptions {
  /** Real wall time source in ms since the epoch (app: `() => Date.now()`). Default: always 0 (tests, Node sims). */
  wallSource?: () => number;
  /** Initial presentation and logic instant (rounded down to whole ticks for logic). Default 0. */
  startMs?: number;
}

export class Clock {
  /** 1000 / 60. */
  static readonly SIM_DT: number = SIM_DT;

  private ticks = 0;
  private present = 0;
  private scale = 1;
  private stepped = false;
  private wallOffset = 0;
  private readonly wallSource: () => number;

  /** Pause flag: `advance()` is a no-op while paused; `step()`/`seek()` still work. */
  paused = false;

  constructor(opts: ClockOptions = {}) {
    this.wallSource = opts.wallSource ?? (() => 0);
    const start = Math.max(0, opts.startMs ?? 0);
    this.ticks = Math.floor(start / SIM_DT + FRONTIER_EPS);
    this.present = start;
  }

  /** Logic frontier: a whole multiple of SIM_DT, advanced only by `commitTick()`. */
  get simMs(): number { return (this.ticks * 1000) / 60; }
  /** Number of committed logic ticks (`simMs / SIM_DT`). */
  get tickIndex(): number { return this.ticks; }
  /** The displayed instant, continuous. */
  get presentMs(): number { return this.present; }
  /** 1 − (simMs − presentMs) / SIM_DT ∈ (0, 1] — for UI tweens only; animation never uses it. */
  get alpha(): number { return 1 - (this.simMs - this.present) / SIM_DT; }

  /** Clock scale (0.05..8); values outside are clamped, NaN is ignored. */
  get timeScale(): number { return this.scale; }
  set timeScale(k: number) {
    if (Number.isNaN(k)) return;
    this.scale = Math.min(TIME_SCALE_MAX, Math.max(TIME_SCALE_MIN, k));
  }

  /** presentMs += dtMs × timeScale (no-op while paused); dtMs = real dt or 1000/cadence. */
  advance(dtMs: number): void {
    if (this.paused || !(dtMs > 0)) return;
    this.present += dtMs * this.scale;
  }

  /** True once after `step()`/`seek()`: the loop then runs every tick it needs, uncapped. */
  consumeStepped(): boolean {
    const s = this.stepped;
    this.stepped = false;
    return s;
  }

  /** simMs += SIM_DT. */
  commitTick(): void { this.ticks++; }

  /** After the per-frame tick cap: presentMs := simMs (time dilates, never skips). */
  dropBacklog(): void { this.present = this.simMs; }

  /** Debug, also while paused: presentMs += ms EXACTLY (any ms ≥ 0, e.g. 1000/144; no tick rounding). */
  step(ms: number): void {
    if (!(ms >= 0) || !Number.isFinite(ms)) throw new RangeError(`Clock.step: ms must be a finite number ≥ 0, got ${ms}`);
    this.present += ms;
    this.stepped = true;
  }

  /** Debug: presentMs := ms, forward only (backward = screen restart + seek, architecture §8.2). */
  seek(ms: number): void {
    if (!Number.isFinite(ms)) throw new RangeError(`Clock.seek: ms must be finite, got ${ms}`);
    if (ms < this.present) throw new RangeError(`Clock.seek: backward seek ${ms} < ${this.present}; restart the screen and seek forward`);
    this.present = ms;
    this.stepped = true;
  }

  /** Wall time = wall source + offset (app layer only; injected into pure code). */
  wallNow(): number { return this.wallSource() + this.wallOffset; }
  /** `__marg.timeTravel` / `?wall=`: shifts the wall clock (ms < 0 allowed). */
  setWallOffset(ms: number): void { this.wallOffset = ms; }
  /** Current wall offset, ms. */
  get wallOffsetMs(): number { return this.wallOffset; }
}

/** Result of one {@link runLogicFrame}. */
export interface LogicFrameResult {
  /** Logic ticks run this frame. */
  ticks: number;
  /** True when the tick cap was hit and the backlog dropped (`perf.slowFrames++`). */
  slow: boolean;
}

/**
 * The logic half of one rAF frame (architecture §2.3), shared by `src/app/loop.ts` and the clock tests:
 * clamp dt to {@link MAX_FRAME_DT} → `advance` → run ticks until `simMs` covers `presentMs` (capped at
 * {@link MAX_TICKS_PER_FRAME} unless the frame was stepped) → drop the backlog if the cap was hit.
 * Afterwards `presentMs ≤ simMs < presentMs + SIM_DT` (within {@link FRONTIER_EPS}).
 *
 * @param dtMs real frame dt, or `1000 / cadence` under `?cadence=` (already jittered by the caller)
 * @param tick called once per logic tick BEFORE `commitTick()` (the screen's `update(SIM_DT)`)
 */
export function runLogicFrame(clock: Clock, dtMs: number, tick: (dtMs: number) => void): LogicFrameResult {
  clock.advance(Math.min(MAX_FRAME_DT, Math.max(0, dtMs)));
  const cap = clock.consumeStepped() ? Infinity : MAX_TICKS_PER_FRAME;
  let n = 0;
  for (; clock.simMs < clock.presentMs - FRONTIER_EPS && n < cap; n++) {
    tick(SIM_DT);
    clock.commitTick();
  }
  let slow = false;
  if (clock.simMs < clock.presentMs - FRONTIER_EPS) {
    clock.dropBacklog();
    slow = true;
  }
  return { ticks: n, slow };
}
